(function () {
  'use strict';

  function normalizeOptionName(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  function parseAliases(value) {
    return String(value || '')
      .split(',')
      .map(normalizeOptionName)
      .filter(Boolean);
  }

  function findSizeOptionIndex(product, aliases) {
    var configured = normalizeOptionName(product.configuredSizeOption);
    if (configured) {
      return product.options.findIndex(function (option) {
        return normalizeOptionName(option.name) === configured;
      });
    }

    return product.options.findIndex(function (option) {
      return aliases.indexOf(normalizeOptionName(option.name)) !== -1;
    });
  }

  function variantsForSelections(product, sizeIndex, selections) {
    return product.variants.filter(function (variant) {
      return product.options.every(function (_option, optionIndex) {
        if (optionIndex === sizeIndex) return true;
        return variant.options[optionIndex] === selections[optionIndex];
      });
    });
  }

  function sizeRowsForSelections(product, sizeIndex, selections) {
    var candidates = variantsForSelections(product, sizeIndex, selections);
    return product.options[sizeIndex].values.reduce(function (rows, size) {
      var matches = candidates.filter(function (variant) {
        return variant.options[sizeIndex] === size;
      });

      if (matches.length === 1) {
        rows.push({ size: size, variant: matches[0], ambiguous: false });
      } else if (matches.length > 1) {
        rows.push({ size: size, variant: null, ambiguous: true });
      }
      return rows;
    }, []);
  }

  function formatMoney(cents, locale, currency) {
    try {
      return new Intl.NumberFormat(locale || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return ((Number(cents || 0) / 100).toFixed(2) + ' ' + (currency || 'USD')).trim();
    }
  }

  function makeElement(tag, className, text) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text !== 'undefined') element.textContent = text;
    return element;
  }

  function safeJson(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (_error) {
      return null;
    }
  }

  function randomBundleId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'mrb-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
  }

  function rootPath() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  function cartUrl(path) {
    if (!path) return rootPath() + 'cart.js';
    if (/^https?:\/\//i.test(path)) return path;
    return path.charAt(0) === '/' ? path : rootPath() + path;
  }

  var HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

  class RingBundleBuilder extends HTMLElementBase {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.currency = this.dataset.currency || 'USD';
      this.defaultSize = this.dataset.defaultSize || '';
      this.aliases = parseAliases(this.dataset.sizeAliases || 'Size,Ring size,US Size');
      this.components = [];
      this.inFlight = false;
      this.addButton = this.querySelector('[data-mrb-add]');
      this.message = this.querySelector('[data-mrb-message]');

      this.querySelectorAll('[data-mrb-component]').forEach((card) => {
        this.initializeComponent(card);
      });

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addCompleteSet());
      }
      this.updateBundleState();
    }

    initializeComponent(card) {
      var product = safeJson(card.querySelector('[data-mrb-component-json]'));
      var component = {
        card: card,
        product: product,
        selections: {},
        sizeRows: [],
        sizeIndex: -1,
        selectedSize: '',
        selectedVariant: null,
        error: ''
      };
      this.components.push(component);

      if (!product || !Array.isArray(product.options) || !Array.isArray(product.variants)) {
        this.setComponentError(component, 'This product could not be loaded.');
        return;
      }

      component.sizeIndex = findSizeOptionIndex(product, this.aliases);
      if (component.sizeIndex < 0) {
        this.setComponentError(
          component,
          'Size option not found. Set custom.bundle_size_option_name on this product.'
        );
        return;
      }

      var initialVariant = product.variants.find((variant) => {
        return variant.available && variant.options[component.sizeIndex] === this.defaultSize;
      }) || product.variants.find(function (variant) {
        return variant.available;
      }) || product.variants[0];

      product.options.forEach(function (_option, optionIndex) {
        if (optionIndex !== component.sizeIndex) {
          component.selections[optionIndex] = initialVariant && initialVariant.options[optionIndex];
        }
      });
      component.selectedSize = initialVariant ? initialVariant.options[component.sizeIndex] : '';

      this.renderExtraOptions(component);
      this.renderSlider(component);
    }

    renderExtraOptions(component) {
      var container = component.card.querySelector('[data-mrb-extra-options]');
      if (!container) return;
      container.textContent = '';

      component.product.options.forEach((option, optionIndex) => {
        if (optionIndex === component.sizeIndex) return;

        var field = makeElement('label', 'mrb-component__option');
        var label = makeElement('span', '', option.name);
        var select = makeElement('select', 'mrb-component__select');
        select.setAttribute('aria-label', option.name);

        option.values.forEach(function (value) {
          var optionElement = makeElement('option', '', value);
          optionElement.value = value;
          optionElement.selected = component.selections[optionIndex] === value;
          select.appendChild(optionElement);
        });

        select.addEventListener('change', () => {
          component.selections[optionIndex] = select.value;
          this.renderSlider(component);
          this.updateBundleState();
        });

        field.appendChild(label);
        field.appendChild(select);
        container.appendChild(field);
      });
    }

    renderSlider(component) {
      var container = component.card.querySelector('[data-mrb-slider]');
      if (!container) return;
      container.textContent = '';
      component.error = '';
      component.sizeRows = sizeRowsForSelections(
        component.product,
        component.sizeIndex,
        component.selections
      );

      if (!component.sizeRows.length) {
        this.setComponentError(component, 'No size variants exist for the selected options.');
        return;
      }

      if (component.sizeRows.some(function (row) { return row.ambiguous; })) {
        this.setComponentError(component, 'Variant mapping is ambiguous. Check this product’s options.');
        return;
      }

      var selectedIndex = component.sizeRows.findIndex((row) => {
        return row.size === component.selectedSize;
      });
      if (selectedIndex < 0) {
        selectedIndex = component.sizeRows.findIndex((row) => {
          return row.size === this.defaultSize && row.variant.available;
        });
      }
      if (selectedIndex < 0) {
        selectedIndex = component.sizeRows.findIndex(function (row) {
          return row.variant.available;
        });
      }
      if (selectedIndex < 0) selectedIndex = 0;

      var inputId = 'mrb-range-' + this.dataset.sectionId + '-' + component.product.productId;
      var range = makeElement('input', 'mrb-size-range');
      range.type = 'range';
      range.id = inputId;
      range.min = '0';
      range.max = String(component.sizeRows.length - 1);
      range.step = '1';
      range.value = String(selectedIndex);
      range.setAttribute('aria-label', component.product.title + ' size');
      range.setAttribute('aria-valuetext', 'US ' + component.sizeRows[selectedIndex].size);

      var ticks = makeElement('div', 'mrb-size-ticks');
      component.sizeRows.forEach((row, index) => {
        var tick = makeElement('button', 'mrb-size-tick', row.size);
        tick.type = 'button';
        tick.dataset.index = String(index);
        tick.setAttribute('aria-label', 'US ' + row.size + (row.variant.available ? '' : ', sold out'));
        if (!row.variant.available) tick.classList.add('is-sold-out');
        tick.addEventListener('click', () => {
          range.value = String(index);
          this.selectSize(component, index, range, ticks);
        });
        ticks.appendChild(tick);
      });

      range.addEventListener('input', () => {
        this.selectSize(component, Number(range.value), range, ticks);
      });
      range.addEventListener('change', () => {
        this.selectSize(component, Number(range.value), range, ticks);
      });

      container.appendChild(range);
      container.appendChild(ticks);
      this.selectSize(component, selectedIndex, range, ticks);
    }

    selectSize(component, index, range, ticks) {
      var row = component.sizeRows[index];
      if (!row) return;
      component.selectedSize = row.size;
      component.selectedVariant = row.variant;
      range.setAttribute('aria-valuetext', 'US ' + row.size + (row.variant.available ? '' : ', sold out'));

      ticks.querySelectorAll('.mrb-size-tick').forEach(function (tick, tickIndex) {
        tick.classList.toggle('is-selected', tickIndex === index);
        tick.setAttribute('aria-pressed', tickIndex === index ? 'true' : 'false');
      });

      var sizeOutput = component.card.querySelector('[data-mrb-selected-size]');
      if (sizeOutput) sizeOutput.textContent = 'US ' + row.size;

      var status = component.card.querySelector('[data-mrb-status]');
      if (status) {
        status.textContent = row.variant.available ? 'In stock' : 'Sold out in this size';
        status.classList.toggle('is-unavailable', !row.variant.available);
      }

      var price = component.card.querySelector('[data-mrb-component-price]');
      if (price) price.textContent = formatMoney(row.variant.price, this.locale, this.currency);
      this.updateBundleState();
    }

    setComponentError(component, message) {
      component.error = message;
      component.selectedVariant = null;
      var status = component.card.querySelector('[data-mrb-status]');
      if (status) {
        status.textContent = message;
        status.classList.add('is-unavailable');
      }
      this.updateBundleState();
    }

    updateBundleState() {
      var complete = this.components.length > 0 && this.components.every(function (component) {
        return !component.error && component.selectedVariant && component.selectedVariant.available;
      });
      var total = this.components.reduce(function (sum, component) {
        return sum + (component.selectedVariant ? Number(component.selectedVariant.price || 0) : 0);
      }, 0);

      this.querySelectorAll('[data-mrb-total]').forEach((element) => {
        element.textContent = formatMoney(total, this.locale, this.currency);
      });
      if (this.addButton) this.addButton.disabled = !complete || this.inFlight;
    }

    async addCompleteSet() {
      if (this.inFlight || !this.addButton) return;
      var valid = this.components.length > 0 && this.components.every(function (component) {
        return component.selectedVariant && component.selectedVariant.available && !component.error;
      });
      if (!valid) {
        this.showMessage('Choose an in-stock size for every ring.', true);
        return;
      }

      var parentVariantId = String(this.dataset.parentVariantId || '');
      if (!/^\d+$/.test(parentVariantId)) {
        this.showMessage('The bundle parent variant is not configured.', true);
        return;
      }

      this.inFlight = true;
      this.setLoading(true);
      this.showMessage('', false);
      var bundleId = randomBundleId();
      var bundleTitle = this.dataset.bundleTitle || 'Ring bundle';
      var componentCount = String(this.components.length);
      var items = this.components.map(function (component, index) {
        return {
          id: Number(component.selectedVariant.id),
          quantity: 1,
          properties: {
            '_mrb_bundle_id': bundleId,
            '_mrb_parent_variant_id': parentVariantId,
            '_mrb_bundle_title': bundleTitle,
            '_mrb_component_count': componentCount,
            '_mrb_component_index': String(index + 1)
          }
        };
      });

      try {
        var addResponse = await fetch(cartUrl(this.dataset.cartAddUrl || 'cart/add.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: items })
        });
        var addPayload = await addResponse.json().catch(function () { return {}; });
        if (!addResponse.ok) {
          throw new Error(addPayload.description || addPayload.message || 'One or more sizes are no longer available.');
        }

        var cartResponse = await fetch(cartUrl(this.dataset.cartJsonUrl || 'cart.js'), {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });
        var cart = cartResponse.ok ? await cartResponse.json() : null;
        this.notifyTheme(cart, addPayload);

        var viewCart = document.createElement('a');
        viewCart.href = this.dataset.cartUrl || cartUrl('cart');
        viewCart.textContent = 'View cart';
        this.showMessage('Complete set added. ', false, viewCart);
      } catch (error) {
        this.showMessage(error.message || 'The set could not be added. Please refresh and try again.', true);
      } finally {
        this.inFlight = false;
        this.setLoading(false);
        this.updateBundleState();
      }
    }

    notifyTheme(cart, addPayload) {
      var detail = { cart: cart, response: addPayload, source: 'mrb-ring-bundle-builder' };
            if (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (element) {
          element.textContent = String(cart.item_count || 0);
          element.classList.toggle('t4s-op-0', !cart.item_count);
        });

        document.querySelectorAll('[data-cart-tt-price]').forEach((element) => {
          element.textContent = formatMoney(cart.total_price, this.locale, this.currency);
        });
      }

      window.setTimeout(function () {
        var drawerTrigger = document.querySelector('a[data-drawer-options*="#t4s-mini_cart"]');
        if (drawerTrigger && document.querySelector('#t4s-mini_cart')) drawerTrigger.click();
      }, 1000);
['cart:updated', 'cart:refresh', 'theme:cart:change', 't4s:cart:refresh'].forEach(function (name) {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
      });
    }

    setLoading(loading) {
      this.addButton.setAttribute('aria-busy', loading ? 'true' : 'false');
      var label = this.addButton.querySelector('[data-mrb-add-label]');
      var loadingLabel = this.addButton.querySelector('[data-mrb-add-loading]');
      if (label) label.hidden = loading;
      if (loadingLabel) loadingLabel.hidden = !loading;
      this.addButton.disabled = loading;
    }

    showMessage(text, isError, appendedNode) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.classList.toggle('is-error', Boolean(isError));
      if (appendedNode) this.message.appendChild(appendedNode);
    }
  }

  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('mrb-ring-bundle')) {
    window.customElements.define('mrb-ring-bundle', RingBundleBuilder);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      normalizeOptionName: normalizeOptionName,
      parseAliases: parseAliases,
      findSizeOptionIndex: findSizeOptionIndex,
      variantsForSelections: variantsForSelections,
      sizeRowsForSelections: sizeRowsForSelections,
      formatMoney: formatMoney
    };
  }
})();
